import { computed, shallowRef } from "vue";
import type { Component } from "vue";

export type OverlayKind = "confirm" | "blocking" | "modal" | "sheet" | "popover";
export type OverlayCloseReason = "esc" | "action" | "api";

export type OverlayEntry<TProps extends Record<string, unknown> = Record<string, unknown>> = {
  id: string;
  kind: OverlayKind;
  component: Component;
  props?: TProps;
  closeOnEsc?: boolean;
  onClose?: (reason: OverlayCloseReason) => void;
};

const overlays = shallowRef<OverlayEntry[]>([]);
let overlaySeq = 0;

export function useOverlayStack() {
  const ordered = computed(() => overlays.value.slice());

  function push(entry: Omit<OverlayEntry, "id"> & { id?: string }): string {
    const id = entry.id ?? `ov_${overlaySeq++}`;
    overlays.value = [...overlays.value, { closeOnEsc: true, ...entry, id }];
    return id;
  }

  function update(id: string, patch: Partial<Omit<OverlayEntry, "id">>) {
    const next = overlays.value.map((entry) => {
      if (entry.id !== id) return entry;
      return { ...entry, ...patch, id: entry.id };
    });
    overlays.value = next;
  }

  function close(id: string, reason: OverlayCloseReason = "api") {
    const entry = overlays.value.find((o) => o.id === id);
    entry?.onClose?.(reason);
    overlays.value = overlays.value.filter((o) => o.id !== id);
  }

  function closeTop() {
    const priority: OverlayKind[] = ["confirm", "blocking", "modal", "sheet", "popover"];
    for (const kind of priority) {
      for (let i = overlays.value.length - 1; i >= 0; i--) {
        const entry = overlays.value[i];
        if (entry.kind !== kind) continue;
        if (entry.closeOnEsc === false) return false;
        close(entry.id, "esc");
        return true;
      }
    }
    return false;
  }

  function clear() {
    overlays.value.forEach((o) => o.onClose?.("api"));
    overlays.value = [];
  }

  const hasOverlays = computed(() => overlays.value.length > 0);
  const top = computed(() => overlays.value.at(-1) ?? null);

  return { overlays: ordered, hasOverlays, top, push, update, close, closeTop, clear };
}

export function overlayZIndex(kind: OverlayKind): number {
  switch (kind) {
    case "confirm":
      return 4200;
    case "blocking":
      return 4100;
    case "modal":
      return 4000;
    case "sheet":
      return 3900;
    case "popover":
      return 3800;
    default:
      return 4000;
  }
}
